import {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Drawer,
  Link,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCourseContent,
  selectCourseContentByID,
  selectCourseContentStatusByID,
} from 'src/v3/classroom-v2/classroom-v2.slice'
import {
  markShowNotesStepCompleted,
  resetWizard,
  reviewContentFinished,
  reviewContentRequested,
  selectActiveStepIndexByCourseID,
  selectCourseProgressStatusByID,
  selectExplorationModeByCourseID,
  selectIsAssessmentReachable,
  selectIsCourseFullyComplete,
  selectIsReviewingContentByCourseID,
  selectMarkCompleteStatusByID,
  selectSessionCompletedStepIndicesByID,
  selectSessionProgressByID,
  selectStartQuizStatusByID,
  selectWizardPhase,
  setActiveStepIndex,
  setExplorationMode,
  startQuizSession,
  videoProgressMade,
  videoStepCompleted,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'
import {
  CourseStepper,
  CourseStepperSkeleton,
} from 'src/v3/classroom-v2/components/course-stepper'
import { ShowNotesStep } from 'src/v3/classroom-v2/components/show-notes-step'
import {
  VideoPlayer,
  VideoPlayerSkeleton,
} from 'src/v3/classroom-v2/components/video-player'
import { ComplianceVideoStep } from 'src/v3/classroom-v2/components/compliance-video-step'
import { VideoDescriptionTabs } from 'src/v3/classroom-v2/components/video-description-tabs'
import {
  CourseOverviewSkeleton,
  CourseStepSkeleton,
} from 'src/v3/classroom-v2/components/overview-skeleton'
import { CoursePhaseStepper } from 'src/v3/classroom-v2/components/course-phase-stepper'
import { PreQuizStep } from 'src/v3/classroom-v2/components/pre-quiz-step'
import { QuizStep } from 'src/v3/classroom-v2/components/quiz-step'
import { SurveyStep } from 'src/v3/classroom-v2/components/survey-step'
import { CertificateStep } from 'src/v3/classroom-v2/components/certificate-step'
import { NotYetAvailable } from 'src/v3/classroom-v2/components/not-yet-available'
import { selectUserID } from 'src/v3/settings/settings.slice'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { CourseV2WizardPhase } from 'src/v3/classroom-v2/types'
import {
  BulletedListIcon,
  ConfirmIcon,
  LeftArrowIcon,
  RightArrowIcon,
} from 'src/v3/core/icons'

let STEPPER_WIDTH = 340

// Anything that's really wizard state — active step, progress, exploration
// mode, mark-complete status — lives in classroom-v2-wizard.slice.ts and its
// middleware, not in local useState/useEffect chains here. A dispatch just
// announces what happened; the slice and middleware decide what follows
// (resetting on mount, refetching progress, clearing stale request status on
// step change, etc). Keeping that in one direction — component dispatches,
// store computes, component re-renders — is what keeps this file from
// turning into a pile of effects racing each other to react to each other.
export default (): ReactElement => {
  let { courseID } = useParams()
  let dispatch = useAppDispatch()

  let userID = useAppSelector(selectUserID)
  let content = useAppSelector(selectCourseContentByID(courseID ?? ''))
  let status = useAppSelector(selectCourseContentStatusByID(courseID ?? ''))
  let progressStatus = useAppSelector(
    selectCourseProgressStatusByID(courseID ?? ''),
  )
  let sessionProgress = useAppSelector(
    selectSessionProgressByID(courseID ?? ''),
  )
  let completedStepIndices = useAppSelector(
    selectSessionCompletedStepIndicesByID(courseID ?? ''),
  )
  let explorationMode = useAppSelector(
    selectExplorationModeByCourseID(courseID ?? ''),
  )
  // A course that's already fully complete defaults its video steps into
  // local review — no dispatch, no touching the shared exploration mode
  // flag, since that would trigger the mode-change middleware's progress
  // wipe and erase the completed state this is meant to display. This is
  // strictly a rendering-time override for the video branch below, never
  // written to Redux, and never surfaced as a "you're in exploration mode"
  // switch flip the user didn't ask for.
  let isCourseFullyComplete = useAppSelector(
    selectIsCourseFullyComplete(courseID ?? ''),
  )
  let markCompleteStatus = useAppSelector(
    selectMarkCompleteStatusByID(courseID ?? ''),
  )
  // In-memory only — never a route param. Deep-linking directly to a step
  // would let a user bypass earlier steps, which is a compliance threat
  // vector this wizard intentionally avoids by having no per-step URL. Lives
  // in the wizard slice (not local state) so resetWizard can clear it on
  // mount, and so selecting a step can clear mark-complete status for the
  // new step in the same reducer, with no extra effect needed.
  let activeStepIndex = useAppSelector(
    selectActiveStepIndexByCourseID(courseID ?? ''),
  )
  // Which surface renders, derived entirely from the server's progress record
  // (plus the one-way content-review flag). Not stored, not routable, not
  // settable — see selectWizardPhase for why each gate is where it is. null
  // means progress hasn't hydrated yet.
  let phase = useAppSelector(selectWizardPhase(courseID ?? ''))
  let isReviewingContent = useAppSelector(
    selectIsReviewingContentByCourseID(courseID ?? ''),
  )
  let startQuizStatus = useAppSelector(
    selectStartQuizStatusByID(courseID ?? ''),
  )
  // Whether the stepper's Final Milestone and the last step's forward button
  // are live destinations rather than inert previews. Derived in the slice from
  // the same gate that decides what renders, so an affordance can never offer
  // a phase selectWizardPhase would refuse — including in exploration mode,
  // where it's always false.
  let isAssessmentReachable = useAppSelector(
    selectIsAssessmentReachable(courseID ?? ''),
  )

  let [isStepperOpen, setIsStepperOpen] = useState(false)

  // This route never navigates or remounts as the learner moves between
  // steps and phases — it's one mounted component swapping what it renders
  // internally — so there's no "on mount" for a plain useScrollToTop() to
  // hook into, and the window itself doesn't scroll here anyway (see
  // contentAreaRef below). Re-fires on every step change within content and
  // on every phase change (content → preQuiz → quiz → survey → certificate),
  // which is every point this component swaps what's on screen for something
  // that starts below the fold.
  let contentAreaRef = useRef<HTMLDivElement>(null)
  useScrollToTop(contentAreaRef, [activeStepIndex, phase?.kind])

  // The wizard slice must never carry state across visits — progress,
  // exploration mode, active step, and mark-complete status are all reset
  // fresh every time the wizard is entered, even for the same course.
  // classroom-v2.middleware.ts listens for this action and triggers the
  // matching progress refetch.
  //
  // useLayoutEffect, not useEffect, and that is load-bearing rather than a
  // micro-optimization. Redux is an external store this component does not
  // control the timing of: with a passive effect, the first render of a REVISIT
  // reads the previous visit's progress record — which is still sitting in the
  // store, fully hydrated — so selectWizardPhase resolves a real phase from it
  // and the player paints that surface. The user sees a stale checkpoint screen
  // for the whole refetch round trip before the fresh record replaces it. That
  // is the "it flashes the other page then routes me into the quiz" report: not
  // a phase gate misfiring, just this component rendering last visit's answer.
  // Wiping before paint means a revisit shows the skeleton until the server has
  // actually answered, which is what every other path here already promises.
  useLayoutEffect(() => {
    if (courseID) {
      dispatch(resetWizard(courseID))
    }
  }, [courseID])

  useEffect(() => {
    if (courseID && status === 'uninitialized') {
      dispatch(fetchCourseContent(courseID))
    }
  }, [courseID, status])

  // Landing on the first incomplete step is handled by
  // classroom-v2.middleware.ts once content and progress have both
  // settled — content and progress fetches race independently, so the
  // middleware reacts to whichever resolves second.

  if (status === 'rejected') {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h1">Course unavailable</Typography>
        <Typography color="text.secondary">
          We couldn&apos;t load this course. Please try again later.
        </Typography>
      </Box>
    )
  }

  if (!content) {
    return <CourseOverviewSkeleton />
  }

  let isProgressLoading =
    !explorationMode &&
    progressStatus !== 'fulfilled' &&
    progressStatus !== 'rejected'

  let activeStep =
    activeStepIndex != null ? content.steps[activeStepIndex] : null
  let isActiveStepCompleted =
    activeStepIndex != null && completedStepIndices.includes(activeStepIndex)
  // Rehash gating (per classroom-v2-wizard.slice.ts's design notes): a step
  // that's already completed must render review-only regardless of whether
  // the rest of the course is done — an individually-completed video should
  // never re-enter gating/telemetry on replay. Course-wide completion and
  // exploration mode are the other two review triggers; all three compose
  // with OR since any one of them alone is sufficient reason to review
  // rather than record.
  let isVideoStepInReviewMode =
    explorationMode || isCourseFullyComplete || isActiveStepCompleted
  // Free-roam (and, by the same reasoning, this local review override)
  // cares nothing about the user's progress — it's a pure review surface,
  // always starting from the beginning, with no server state read or
  // written. Resuming from a saved position is a compliance-mode-only
  // concept.
  let activeStepProgress = sessionProgress?.steps.find(
    (s) => s.stepIndex === activeStepIndex,
  )
  let activeStepVideoProgressSeconds =
    !isVideoStepInReviewMode && activeStepProgress?.kind === 'videoStep'
      ? (activeStepProgress.progressSeconds ?? 0)
      : 0
  let hasPrevious = activeStepIndex != null && activeStepIndex > 0
  let isOnLastStep =
    activeStepIndex != null && activeStepIndex === content.steps.length - 1
  let hasNext =
    activeStepIndex != null &&
    activeStepIndex < content.steps.length - 1 &&
    isActiveStepCompleted

  let handleMarkComplete = () => {
    if (
      activeStepIndex == null ||
      !courseID ||
      !activeStep ||
      activeStep.type !== 'show_notes'
    ) {
      return
    }
    dispatch(
      markShowNotesStepCompleted({
        courseID,
        stepIndex: activeStepIndex,
        showNotesID: activeStep.id,
      }),
    )
  }

  let goToStep = (stepIndex: number) => {
    if (!courseID) return
    dispatch(setActiveStepIndex({ courseID, stepIndex }))
  }

  // Recovery from ENG-607's prior-steps-incomplete rejection: unlike
  // insufficient progress, the problem isn't this video, so send the user
  // back to the first step the server doesn't have a completion record
  // for — mirrors classroom-v2.middleware.ts's own firstIncomplete logic
  // for landing on mount.
  let goToFirstIncompleteStep = () => {
    let firstIncomplete = content.steps.findIndex(
      (_, i) => !completedStepIndices.includes(i),
    )
    goToStep(firstIncomplete === -1 ? 0 : firstIncomplete)
  }

  // The session endpoint derives the quiz from the course itself, so there's no
  // quizID to pass and no way for the client to name a different quiz than the
  // one the course actually has.
  let handleStartQuiz = () => {
    if (!courseID) return
    dispatch(startQuizSession(courseID))
  }

  // The pre-quiz screen keeps the step sidebar: the user isn't inside the
  // assessment yet, so the course's steps are still useful context and a
  // legitimate place to go back to. Nothing is lost safety-wise, because the
  // stepper's only navigation is per-step selection, and setActiveStepIndex
  // already rejects any index the user's progress doesn't justify. What the
  // checkpoint does drop is the previous/next bar, which is per-step furniture
  // with nothing to advance to here.
  let phaseContent = (
    checkpoint: Exclude<CourseV2WizardPhase, { kind: 'content' }>,
  ): ReactElement => {
    // A checkpoint phase can only be derived from a hydrated progress record,
    // so this is unreachable; it keeps sessionProgress non-null for the props
    // below without an assertion.
    if (!sessionProgress) return <CourseOverviewSkeleton />

    if (checkpoint.kind === 'preQuiz') {
      // allStepsCompleted is true but the course has no quiz attached. The
      // phase gate can't produce a meaningful assessment here, so say so
      // rather than rendering a CTA that would POST a null quizID.
      if (!content.quiz) {
        return (
          <Box sx={{ maxWidth: 720, mx: 'auto' }}>
            <NotYetAvailable label="Course assessment" height={200} />
          </Box>
        )
      }
      return (
        <PreQuizStep
          content={content}
          quiz={content.quiz}
          progress={sessionProgress}
          isStarting={startQuizStatus === 'pending'}
          hasStartFailed={startQuizStatus === 'rejected'}
          onStartQuiz={handleStartQuiz}
          onReviewContent={() =>
            courseID && dispatch(reviewContentRequested(courseID))
          }
        />
      )
    }

    // The quiz owns its own loading/error/scored states internally (see the
    // CourseV2QuizAttempt union), so this hands off rather than branching. The
    // sessionID from the phase isn't threaded through: QuizStep re-opens the
    // session itself, which is the same reuse-not-create call and the only path
    // that works on a cold reload straight into this phase.
    if (checkpoint.kind === 'quiz') {
      return (
        <QuizStep
          courseID={courseID ?? ''}
          userID={userID}
          isStartingRetake={startQuizStatus === 'pending'}
        />
      )
    }

    // Like the quiz, the survey owns its internal states through the
    // CourseV2SurveyAttempt union — draft, in flight, submitted, prerequisites
    // still owed — so this hands off rather than branching. It covers both survey
    // phases the gate can produce: notSubmitted (the form) and locked (what's
    // still owed), which is why nothing about openness is re-derived here.
    if (checkpoint.kind === 'survey') {
      return <SurveyStep courseID={courseID ?? ''} />
    }

    // Same hand-off as the quiz and survey: the certificate owns its internal
    // states through the CourseV2CertificatePoll union — waiting on generation,
    // issued, requirements still owed, unreachable — and it starts its own poll
    // by announcing that it's showing. Nothing about whether a certificate exists
    // is decided here; the phase gate only says the learner has earned one.
    return <CertificateStep courseID={courseID ?? ''} content={content} />
  }

  // A checkpoint phase renders in place of the step body, inside the same
  // shell, so the sidebar stays put. Narrowed here rather than in the JSX so
  // sessionProgress and the non-content phase are both non-null below.
  let checkpoint =
    phase !== null && phase.kind !== 'content' && sessionProgress ? phase : null

  // Leaving exploration mode from the banner. The same dispatch the sidebar
  // toggle makes, so it inherits the atomic wipe-and-reland in
  // setExplorationMode's reducer — the learner isn't left on a step their
  // recorded progress doesn't justify. Offered here because the sidebar (and
  // therefore the toggle) is behind a drawer on mobile, so the banner would
  // otherwise state a problem with no way to act on it.
  let handleLeaveExplorationMode = () => {
    if (!courseID) return
    dispatch(setExplorationMode({ courseID, enabled: false }))
  }

  // Leaving review is a fact announcement, not navigation: dropping the flag
  // lets selectWizardPhase compute the phase the user's progress actually puts
  // them in. Nothing here names a destination.
  let handleFinishReview = () => {
    if (!courseID) return
    dispatch(reviewContentFinished(courseID))
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 100px)',
        overflow: 'hidden',
      }}
    >
      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box ref={contentAreaRef} sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
          {checkpoint && sessionProgress ? (
            <>
              <CoursePhaseStepper
                progress={sessionProgress}
                phase={checkpoint}
              />
              {phaseContent(checkpoint)}
            </>
          ) : (
            <>
              {/* Exploration mode's consequence, stated where it can't be
                  missed. The sidebar's ModeHeader already says the mode is on,
                  but it's a caption in a panel that doesn't exist below md —
                  and "progress isn't recorded" is a mechanism, not a
                  consequence, so it's named here as the CE credit itself. What
                  a learner stands to lose is the CE credit, so that's what
                  this says, along with the one thing that earns it back.

                  Rendered unconditionally for the mode rather than dismissible:
                  the whole point is that a learner cannot spend an hour in here
                  and later be surprised, and a banner they closed on step one
                  wouldn't prevent that. It also can't collide with the review
                  alert below — exploration mode short-circuits selectWizardPhase
                  to content, so allStepsCompleted is never consulted and
                  isReviewingContent is unreachable. */}
              {explorationMode && (
                <Alert
                  severity="warning"
                  sx={{ mb: 3 }}
                  action={
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      onClick={handleLeaveExplorationMode}
                    >
                      Switch to Compliance Mode
                    </Button>
                  }
                >
                  <AlertTitle>
                    Exploration mode — this does not count toward your CE
                  </AlertTitle>
                  Nothing you watch or read here counts toward your CE hours. To
                  earn continuing education credit for this course, switch to
                  Compliance mode and complete every step there.
                </Alert>
              )}
              {/* One of two ways back out of content review (the stepper's
                  Final Milestone card is the other). reviewContentRequested
                  suppresses the checkpoint phase, so without an exit the user
                  would be stranded in content with a fully-complete course and
                  no route forward — there is deliberately no URL for the
                  assessment to navigate to. */}
              {isReviewingContent && (
                <Alert
                  severity="info"
                  sx={{ mb: 3 }}
                  action={
                    <Button
                      size="small"
                      variant="contained"
                      endIcon={<RightArrowIcon />}
                      onClick={handleFinishReview}
                    >
                      Back to Assessment
                    </Button>
                  }
                >
                  You&apos;ve finished this course&apos;s content and are
                  reviewing it. Head back when you&apos;re ready for the
                  assessment.
                </Alert>
              )}
              {activeStepIndex != null && activeStep ? (
                <>
                  <Typography
                    variant="overline"
                    sx={{ letterSpacing: 1, display: 'block', fontWeight: 700 }}
                  >
                    <Link
                      component={RouterLink}
                      to={`/classroom-v2/${content.courseID}`}
                      color="primary"
                      underline="hover"
                    >
                      {content.name}
                    </Link>
                    {' / '}
                    Step {activeStepIndex + 1} of {content.steps.length}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="h1"
                      sx={{ fontSize: '2rem', lineHeight: 'inherit', mb: 3 }}
                    >
                      {activeStep.name}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={<BulletedListIcon />}
                      onClick={() => setIsStepperOpen(true)}
                      sx={{
                        display: { xs: 'inline-flex', md: 'none' },
                        flexShrink: 0,
                      }}
                    >
                      Course Steps
                    </Button>
                  </Box>

                  {activeStep.type === 'show_notes' && courseID ? (
                    <>
                      <ShowNotesStep courseID={courseID} step={activeStep} />

                      {!explorationMode && (
                        <>
                          <Divider sx={{ mt: 4, mb: 3 }} />
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color={
                                markCompleteStatus === 'rejected'
                                  ? 'error'
                                  : 'text.secondary'
                              }
                            >
                              {markCompleteStatus === 'rejected'
                                ? 'Something went wrong marking this step complete. Please try again.'
                                : isActiveStepCompleted
                                  ? 'Step completed.'
                                  : 'Read through, then mark this step complete to continue.'}
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<ConfirmIcon />}
                              onClick={handleMarkComplete}
                              disabled={
                                isActiveStepCompleted ||
                                markCompleteStatus === 'pending'
                              }
                            >
                              Mark as complete
                            </Button>
                          </Box>
                        </>
                      )}
                    </>
                  ) : activeStep.type === 'video' && courseID ? (
                    <>
                      {isVideoStepInReviewMode ? (
                        <VideoPlayer
                          // Remounted whenever the video changes so all per-video
                          // state (playback refs, gating, throttles) is torn down
                          // and recreated fresh, rather than reset by an effect.
                          // Review (free-roam, or a fully-complete course)
                          // attaches nothing — no handlers, no resume position —
                          // per ENG-433.
                          key={activeStep.id}
                          courseID={courseID}
                          video={activeStep}
                          userID={userID}
                          showReviewModeBadge
                        />
                      ) : (
                        <ComplianceVideoStep
                          key={activeStep.id}
                          courseID={courseID}
                          video={activeStep}
                          userID={userID}
                          maxWatchedPositionSeconds={
                            activeStepVideoProgressSeconds
                          }
                          isStepCompleted={isActiveStepCompleted}
                          onProgressMade={(progressSeconds) =>
                            dispatch(
                              videoProgressMade({
                                courseID,
                                stepIndex: activeStepIndex,
                                progressSeconds,
                              }),
                            )
                          }
                          onCompleted={() =>
                            dispatch(
                              videoStepCompleted({
                                courseID,
                                stepIndex: activeStepIndex,
                              }),
                            )
                          }
                          onPriorStepsIncomplete={goToFirstIncompleteStep}
                        />
                      )}
                      {activeStep.description && (
                        <VideoDescriptionTabs
                          description={activeStep.description}
                        />
                      )}
                    </>
                  ) : (
                    <VideoPlayerSkeleton />
                  )}
                </>
              ) : (
                // No active step yet means content and progress haven't both
                // settled, so the middleware hasn't landed the learner anywhere —
                // a wait, not an empty state, and never a choice to make.
                <CourseStepSkeleton />
              )}
            </>
          )}
        </Box>

        {/* Previous / next bar — per-step furniture, so it's absent on a
            checkpoint phase, which has no next step to advance to. */}
        {!explorationMode && !checkpoint && (
          <Paper
            square
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 4,
              py: 2,
              bgcolor: 'background.paperDark',
            }}
          >
            {isProgressLoading ? (
              <>
                <Skeleton variant="rounded" width={100} height={36} />
                <Skeleton variant="rounded" width={120} height={36} />
              </>
            ) : (
              <>
                <Button
                  startIcon={<LeftArrowIcon />}
                  disabled={!hasPrevious}
                  onClick={() =>
                    activeStepIndex != null && goToStep(activeStepIndex - 1)
                  }
                >
                  Previous
                </Button>
                {/* On the last step, "next" is the assessment rather than
                    another step — but only when the phase gate agrees it's
                    reachable. In exploration mode isAssessmentReachable is
                    false (selectWizardPhase short-circuits free-roam to
                    content), so the button stays disabled exactly as before:
                    none of the quiz, survey, or certificate content exists for
                    that visit, so there is nothing to advance to. */}
                {isOnLastStep && isAssessmentReachable ? (
                  <Button
                    variant="contained"
                    endIcon={<RightArrowIcon />}
                    onClick={handleFinishReview}
                  >
                    Continue to Assessment
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    endIcon={<RightArrowIcon />}
                    disabled={!hasNext}
                    onClick={() =>
                      activeStepIndex != null && goToStep(activeStepIndex + 1)
                    }
                  >
                    Next Step
                  </Button>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>

      {/* Stepper sidebar */}
      <Box
        component="nav"
        aria-label="Course steps"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: STEPPER_WIDTH,
          flexShrink: 0,
          height: '100%',
          borderLeft: '1px solid',
          borderColor: 'divider',
        }}
      >
        {isProgressLoading ? (
          <CourseStepperSkeleton />
        ) : (
          <CourseStepper
            content={content}
            activeStepIndex={activeStepIndex}
            completedStepIndices={completedStepIndices}
            onSelectStep={goToStep}
            isAssessmentActive={checkpoint !== null}
            isCourseComplete={isCourseFullyComplete}
            onGoToAssessment={
              isAssessmentReachable ? handleFinishReview : undefined
            }
          />
        )}
      </Box>

      {/* Mobile stepper slideout */}
      <Drawer
        anchor="right"
        open={isStepperOpen}
        onClose={() => setIsStepperOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
        slotProps={{
          paper: {
            sx: { width: STEPPER_WIDTH, bgcolor: 'background.default' },
          },
        }}
      >
        {isProgressLoading ? (
          <CourseStepperSkeleton />
        ) : (
          <CourseStepper
            content={content}
            activeStepIndex={activeStepIndex}
            completedStepIndices={completedStepIndices}
            onSelectStep={(index) => {
              goToStep(index)
              setIsStepperOpen(false)
            }}
            isAssessmentActive={checkpoint !== null}
            isCourseComplete={isCourseFullyComplete}
            onGoToAssessment={
              isAssessmentReachable
                ? () => {
                    handleFinishReview()
                    setIsStepperOpen(false)
                  }
                : undefined
            }
          />
        )}
      </Drawer>
    </Box>
  )
}
